import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import { Container } from "@pixi/react";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../pixiSettings";
import OverlayReelSet from "../OverlayReelSet/OverlayReelSet";
import WinText from "../../../Components/Animations/WinText/WinText";
import { WIN_TEXT_CONFIG } from "../../../Components/Animations/WinText/WinTextController";
import ReelFront from "../../../Components/Animations/ReelFront/ReelFront";
import ReelFrameDecoration from "../../../Components/Animations/ReelFrameDecoration/ReelFrameDecoration";
import ReelBack from "../../../Components/Animations/ReelBack/ReelBack";
import ReelBackDecoration from "../../../Components/Animations/ReelBackDecoration/ReelBackDecoration";
import WinLinesContainer from "../../Shared/WinLinesReelSet/WinLinesContainer";
import WinBoxContainer from "../../Shared/WinBoxReelSet/WinBoxContainer";
import DimReelSet from "../../Shared/DimReelSet/DimReelSet";
import DividerReelset from "../../Shared/DividerReelset/DividerReelset";
import ReelSet from "../ReelSet/ReelSet";
import InfoOverlay from "../../Shared/InfoOverlay/InfoOverlay";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import TensionReelSet from "../TensionReelSet/TensionReelSet";
import HoldAndWinReelSet from "../../HoldAndWin/HoldAndWinReelSet";
import HoldAndWinOverlayReelSet from "../../HoldAndWin/HoldAndWinOverlayReelSet";
import HoldAndWinTensionOverlaySet from "../../HoldAndWin/HoldAndWinTensionOverlaySet";
import { useSelector } from "react-redux";
import {
  selectHoldAndWinActive,
  selectFreeSpinActive,
  selectFreeRoundsActive,
  selectFreeRoundsEnded,
  selectFreeRoundsCompletionPending,
  selectErrorRecoveryTransitionActive,
  selectErrorRecoveryScreenCovered,
} from "../../../State/selectors";
import { createTensionZoomAnimation } from "../../../utils/animations";
import anime from "animejs";
import HoldAndWinCounter from "../../../Components/Animations/HoldAndWinCounter/HoldAndWinCounter";
import HoldAndWinProgressBar from "../../../Components/Animations/HoldAndWinProgressBar/HoldAndWinProgressBar";
import HoldAndWinCollectorDisplay from "../../../Components/Animations/HoldAndWinCollectorDisplay/HoldAndWinCollectorDisplay";
import HoldAndWinCoinCollect from "../../../Components/Animations/HoldAndWinCoinCollect/HoldAndWinCoinCollect";
import FreeRoundsCounter from "../../../Components/UserInterface/secondary/Displays/FreeRoundsCounter";
import WinDisplay from "../../../Components/UserInterface/primary/Displays/WinDisplay";
import { Settings } from "../../../settings";
import FreeSpinSceneTransition from "../../../Components/Animations/FreeSpin/FreeSpinSceneTransition/FreeSpinSceneTransition";
import FreeSpinReelSet from "../../FreeSpin/FreeSpinReelSet/FreeSpinReelSet";
import FreeSpinOverlayReelset from "../../FreeSpin/FreeSpinOverlayReelset/FreeSpinOverlayReelset";
import FreeSpinMaskReelSet from "../../FreeSpin/FreeSpinMaskReelSet/FreeSpinMaskReelSet";

// Game container Y offset config (multiplied by symbolHeight, subtracted from center)
// Positive values push the reels UP from center
// Exported so BackgroundAnimationController can follow same offsets
export const GAME_CONTAINER_OFFSET_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 0.9,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 0,
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 0.9,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 0,
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 0.6,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 0.325,
  },
  [PixiSettings.DEVICE.DESKTOP]: 0.2,
};

const GameContainer = React.forwardRef((props, ref) => {
  const {
    dimensions,
    symbolWidth,
    symbolHeight,
    dividerDimensions,
    reelSpacing,
    orientation,
    device,
    isMobileSmall,
  } = useDimensions();

  const {
    dimReelSetRef,
    gameContainerRef,
    backgroundAnimationControllerRef,
    backgroundDecorationControllerRef,
    winLinesContainerRef,
    winBoxContainerRef,
    holdAndWinCollectorDisplayRef,
    holdAndWinCoinCollectRef,
    holdAndWinProgressBarRef,
    reelFrameDecorationRef,
    globalLineWinTextRef,
    lineWinTextRef,
    reelRefs,
  } = useReelRefs();
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const freeSpinActive = useSelector(selectFreeSpinActive);
  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const freeRoundsEnded = useSelector(selectFreeRoundsEnded);
  const freeRoundsCompletionPending = useSelector(
    selectFreeRoundsCompletionPending,
  );
  const errorRecoveryTransitionActive = useSelector(
    selectErrorRecoveryTransitionActive,
  );
  const errorRecoveryScreenCovered = useSelector(
    selectErrorRecoveryScreenCovered,
  );
  // const freeSpinsActive = useSelector(selectFreeSpinsActive);
  const containerRef = useRef(null);
  const reelContainerRef = useRef(null); // Inner container for tension zoom effect
  const tensionEffectAnimationRef = useRef(null);
  const backgroundZoomAnimationRef = useRef(null);
  const decorationZoomAnimationRef = useRef(null);

  // Memoize container position calculations
  // Use real device (isMobileSmall) to resolve MOBILE_SMALL offset config
  const containerY = useMemo(() => {
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    const deviceConfig = GAME_CONTAINER_OFFSET_CONFIG[realDevice];
    const offsetMultiplier =
      typeof deviceConfig === "number"
        ? deviceConfig
        : (deviceConfig?.[orientation] ?? 0.325);
    return dimensions.height / 2 - symbolHeight * offsetMultiplier;
  }, [dimensions.height, symbolHeight, device, orientation, isMobileSmall]);

  const containerX = useMemo(() => dimensions.width / 2, [dimensions.width]);

  // Memoize Free Rounds display condition
  const shouldShowFreeRoundsCounter = useMemo(
    () =>
      FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
      !freeRoundsEnded &&
      (freeRoundsActive || freeRoundsCompletionPending) &&
      !holdAndWinActive &&
      (!errorRecoveryTransitionActive || errorRecoveryScreenCovered),
    [
      freeRoundsEnded,
      freeRoundsActive,
      freeRoundsCompletionPending,
      holdAndWinActive,
      errorRecoveryTransitionActive,
      errorRecoveryScreenCovered,
    ],
  );

  // Play tension zoom effect (only if tension is enabled)
  const playTensionEffect = useCallback(
    (duration = 1500) => {
      // Skip if tension is disabled or free spins are active
      if (!FEATURES_CONFIG.TENSION.ENABLED || freeSpinActive) {
        return;
      }

      if (reelContainerRef.current && !tensionEffectAnimationRef.current) {
        // Only create zoom animation if REELSET_ANIMATION is enabled
        if (FEATURES_CONFIG.TENSION.REELSET_ANIMATION) {
          tensionEffectAnimationRef.current = createTensionZoomAnimation(
            reelContainerRef.current,
            duration,
          );

          // Disable roundPixels on reel sprites during zoom to prevent jitter
          for (let i = 0; i < reelRefs.current.length; i++) {
            reelRefs.current[i]?.getReelController?.()?.setRoundPixels(false);
          }

          // Also zoom background and scene transition if ENVIRONMENT_ZOOM is enabled
          if (FEATURES_CONFIG.TENSION.ENVIRONMENT_ZOOM) {
            // Zoom the background container in sync
            const bgContainer =
              backgroundAnimationControllerRef.current?.container;
            if (bgContainer && !backgroundZoomAnimationRef.current) {
              bgContainer.pivot.set(
                dimensions.width / 2,
                dimensions.height / 2,
              );
              bgContainer.position.set(
                dimensions.width / 2,
                dimensions.height / 2,
              );
              backgroundZoomAnimationRef.current = createTensionZoomAnimation(
                bgContainer,
                duration,
              );
            }

            // Zoom the decoration container in sync (same animation as background)
            const decorationContainer =
              backgroundDecorationControllerRef.current?.container;
            if (decorationContainer && !decorationZoomAnimationRef.current) {
              decorationContainer.pivot.set(
                dimensions.width / 2,
                dimensions.height / 2,
              );
              decorationContainer.position.set(
                dimensions.width / 2,
                dimensions.height / 2,
              );
              decorationZoomAnimationRef.current = createTensionZoomAnimation(
                decorationContainer,
                duration,
              );
            }
          }
        }

        // Play base_event intro animation (only if BACKGROUND_ANIMATION is enabled)
        if (FEATURES_CONFIG.TENSION.BACKGROUND_ANIMATION) {
          backgroundAnimationControllerRef.current?.playBaseEventIn?.();
        }
      }
    },
    [
      backgroundAnimationControllerRef,
      backgroundDecorationControllerRef,
      freeSpinActive,
      dimensions.width,
      dimensions.height,
      reelRefs,
    ],
  );

  // Stop tension zoom effect smoothly
  // forceStop parameter allows collection to stop the effect even during Hold and Win
  const stopTensionEffect = useCallback(
    (forceStop = false) => {
      // Skip if tension is disabled
      if (!FEATURES_CONFIG.TENSION.ENABLED) {
        return;
      }

      // CRITICAL: Don't stop during Hold and Win UNLESS forceStop is true
      // This allows the effect to persist across spins but still be stopped when collection starts
      if (holdAndWinActive && !forceStop) {
        return;
      }

      if (tensionEffectAnimationRef.current) {
        tensionEffectAnimationRef.current.pause();
        tensionEffectAnimationRef.current = null;

        // Smoothly zoom back to normal scale
        if (reelContainerRef.current) {
          anime({
            targets: reelContainerRef.current.scale,
            x: 1.0,
            y: 1.0,
            duration: 600,
            easing: "easeOutQuad",
            complete: () => {
              // Re-enable roundPixels after zoom-out completes for crisp 1:1 rendering
              for (let i = 0; i < reelRefs.current.length; i++) {
                reelRefs.current[i]
                  ?.getReelController?.()
                  ?.setRoundPixels(true);
              }
            },
          });
        }
      }

      // Also stop background zoom
      if (backgroundZoomAnimationRef.current) {
        backgroundZoomAnimationRef.current.pause();
        backgroundZoomAnimationRef.current = null;

        const bgContainer = backgroundAnimationControllerRef.current?.container;
        if (bgContainer) {
          anime({
            targets: bgContainer.scale,
            x: 1.0,
            y: 1.0,
            duration: 600,
            easing: "easeOutQuad",
            complete: () => {
              // Reset pivot back to (0,0) after zoom-out finishes
              bgContainer.pivot.set(0, 0);
              bgContainer.position.set(0, 0);
            },
          });
        }
      }

      // Also stop decoration zoom
      if (decorationZoomAnimationRef.current) {
        decorationZoomAnimationRef.current.pause();
        decorationZoomAnimationRef.current = null;

        const decorationContainer =
          backgroundDecorationControllerRef.current?.container;
        if (decorationContainer) {
          anime({
            targets: decorationContainer.scale,
            x: 1.0,
            y: 1.0,
            duration: 600,
            easing: "easeOutQuad",
            complete: () => {
              // Reset pivot back to (0,0) after zoom-out finishes
              decorationContainer.pivot.set(0, 0);
              decorationContainer.position.set(0, 0);
            },
          });
        }
      }

      // Play base_event exit animation (only if BACKGROUND_ANIMATION is enabled)
      if (FEATURES_CONFIG.TENSION.BACKGROUND_ANIMATION) {
        if (backgroundAnimationControllerRef.current?.playBaseEventOut) {
          backgroundAnimationControllerRef.current.playBaseEventOut();
        }
      }
    },
    [
      holdAndWinActive,
      backgroundAnimationControllerRef,
      backgroundDecorationControllerRef,
      reelRefs,
    ],
  );

  // Expose play/stop methods via ref
  useImperativeHandle(
    gameContainerRef,
    () => ({
      playTensionEffect,
      stopTensionEffect,
    }),
    [playTensionEffect, stopTensionEffect],
  );

  // Update position when dimensions change and stop animation if active
  useEffect(() => {
    if (containerRef.current) {
      // Update container position when Hold & Win state changes
      containerRef.current.y = containerY;

      // Skip tension animation restart if tension is disabled
      if (!FEATURES_CONFIG.TENSION.ENABLED) {
        return;
      }

      // Note: tension zoom animation is NOT stopped on resize.
      // Scale-based animations are resolution-independent and continue smoothly.
    }
  }, [dimensions.width, dimensions.height, symbolHeight, containerY]);

  return (
    <Container
      ref={containerRef}
      name="game_container"
      x={containerX}
      y={containerY}
      sortableChildren
      eventMode="passive"
      zIndex={5}
    >
      {/* Inner container for shakeable elements (reels, symbols, etc.) */}
      <Container
        ref={reelContainerRef}
        name="reel_container"
        x={0}
        y={0}
        sortableChildren
        eventMode="passive"
      >
        {/* Event animations (event_in → event_idle) behind reel frame, after free spin transition */}
        {FEATURES_CONFIG.FREE_SPINS.ENABLED && (
          <ReelBackDecoration
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            dimensions={dimensions}
            dividerDimensions={dividerDimensions}
          />
        )}

        <ReelBack
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dimensions={dimensions}
          dividerDimensions={dividerDimensions}
        />

        <ReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
          reelSpacing={reelSpacing}
        />

        {FEATURES_CONFIG.FREE_SPINS.ENABLED && (
          <FreeSpinReelSet
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            dividerDimensions={dividerDimensions}
            reelSpacing={reelSpacing}
          />
        )}

        {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
          <HoldAndWinReelSet />
        )}

        {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
          <HoldAndWinOverlayReelSet
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            reelSpacing={reelSpacing}
          />
        )}

        {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
          <HoldAndWinTensionOverlaySet />
        )}

        <DimReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
          ref={dimReelSetRef}
        />

        <ReelFront
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dimensions={dimensions}
          dividerDimensions={dividerDimensions}
        />

        {/* Reel Frame Decorations (claws) - Only during Hold and Win */}
        {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
          <ReelFrameDecoration
            ref={reelFrameDecorationRef}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            dimensions={dimensions}
            dividerDimensions={dividerDimensions}
            orientation={orientation}
          />
        )}

        {FEATURES_CONFIG.DIVIDERS.ENABLED && (
          <DividerReelset
            symbolHeight={symbolHeight}
            dividerDimensions={dividerDimensions}
            reelSpacing={reelSpacing}
          />
        )}

        {FEATURES_CONFIG.WIN_BOX.ENABLED && (
          <WinBoxContainer
            ref={winBoxContainerRef}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            reelSpacing={reelSpacing}
          />
        )}

        <OverlayReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          reelSpacing={reelSpacing}
        />

        {FEATURES_CONFIG.FREE_SPINS.ENABLED && <FreeSpinOverlayReelset />}

        {FEATURES_CONFIG.FREE_SPINS.ENABLED && <FreeSpinMaskReelSet />}

        {/* Unified WinText components */}
        {FEATURES_CONFIG.WIN_LINE_DIGIT.ENABLED && (
          <Container name="win_text_overlay" zIndex={6}>
            {/* Global win text - larger font, shown during "show all wins" phase */}
            <WinText
              ref={globalLineWinTextRef}
              fontSizeMultiplier={WIN_TEXT_CONFIG.GLOBAL_WIN_FONT_SIZE}
              hideOnBigWin={true}
            />

            {/* Line win text - smaller font, shown during individual line cycling */}
            <WinText
              ref={lineWinTextRef}
              fontSizeMultiplier={WIN_TEXT_CONFIG.LINE_WIN_FONT_SIZE}
              hideOnBigWin={true}
            />
          </Container>
        )}

        {FEATURES_CONFIG.WIN_LINES.ENABLED && (
          <WinLinesContainer
            ref={winLinesContainerRef}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
          />
        )}

        {FEATURES_CONFIG.TENSION.ENABLED && !holdAndWinActive && (
          <TensionReelSet />
        )}

        <InfoOverlay />

        {/* WinDisplay for mobile portrait — inside reel_container so it zooms with tension */}
        {Settings.APP.UI_THEME === "primary" && (
          <WinDisplay variant="reelset" />
        )}
      </Container>

      {/* Static elements (not affected by shake) */}
      {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
        <HoldAndWinProgressBar ref={holdAndWinProgressBarRef} />
      )}
      {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
        <HoldAndWinCounter />
      )}
      {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
        <HoldAndWinCollectorDisplay ref={holdAndWinCollectorDisplayRef} />
      )}
      {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
        <HoldAndWinCoinCollect ref={holdAndWinCoinCollectRef} />
      )}

      {/* Free Rounds Counter - positioned at bottom center of reels */}
      {shouldShowFreeRoundsCounter && <FreeRoundsCounter />}

      {/* {FEATURES_CONFIG.FREE_SPINS.ENABLED && <FreeSpinGameContainer />} */}

      {/* Free Spin Scene Transition — inside game_container, below UI */}
      {FEATURES_CONFIG.FREE_SPINS.ENABLED && <FreeSpinSceneTransition />}
    </Container>
  );
});

GameContainer.displayName = "GameContainer";

export default GameContainer;
